import { Link } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import Marquee from "react-fast-marquee";
import {
  ArrowRight,
  MessageCircle,
  Check,
  ShieldCheck,
  FileText,
  Users,
  Languages,
  ArrowUpRight,
} from "lucide-react";
import Seo from "../components/Seo";
import { useI18n } from "../i18n/LanguageProvider";
import { useRegisterAlternates } from "../components/layout/Layout";
import { pagePath } from "../config/pages";
import { WHATSAPP_LINK } from "../config/site";
import { TREATMENTS } from "../data/treatments";
import { LOCATIONS } from "../data/locations";
import { ARTICLES } from "../data/articles";
import { FAQS } from "../data/faqs";
import { Reveal, RevealStagger, RevealItem, MaskedLines } from "../components/motion/Reveal";
import { SectionHeading, CTABand } from "../components/Sections";
import { TreatmentCard, LocationCard, ArticleCard } from "../components/Cards";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import {
  organizationSchema,
  websiteSchema,
  faqSchema,
} from "../lib/schema";

const TRUST_ICONS = [ShieldCheck, FileText, Users, Languages];

export default function Home() {
  const { t, lang, p } = useI18n();
  const reduce = useReducedMotion();
  const alternates = { en: pagePath("home", "en"), fr: pagePath("home", "fr"), de: pagePath("home", "de") };
  useRegisterAlternates(alternates);

  const homeFaqs = FAQS.slice(0, 6).map((q) => ({ q: q.q[lang] || q.q.en, a: q.a[lang] || q.a.en }));

  return (
    <>
      <Seo
        title={`${t.hero.h1a} ${t.hero.h1b} ${t.hero.h1c} | Dentel Vietnam`}
        description={t.hero.sub}
        path={p("home")}
        alternates={alternates}
        lang={lang}
        jsonLd={[organizationSchema(), websiteSchema(), faqSchema(homeFaqs)]}
      />

      {/* ============ HERO ============ */}
      <section className="relative overflow-hidden bg-mist" data-testid="hero-section">
        <div
          className="pointer-events-none absolute -left-40 top-0 h-[38rem] w-[38rem] rounded-full opacity-30 blur-3xl"
          style={{ background: "radial-gradient(circle,#14B8A6,transparent 68%)" }}
        />
        <div className="container-dv relative grid gap-12 py-14 lg:grid-cols-12 lg:gap-8 lg:py-24">
          <div className="lg:col-span-6 flex flex-col justify-center">
            <motion.p
              initial={reduce ? false : { opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="label-eyebrow"
            >
              {t.hero.eyebrow}
            </motion.p>

            <h1 className="mt-5 font-serif text-[2.75rem] leading-[1.03] tracking-tight text-navy sm:text-6xl lg:text-[4.25rem]">
              <MaskedLines
                lines={[t.hero.h1a, t.hero.h1b]}
                lineClassName=""
              />
              <span className="text-mask-line">
                {reduce ? (
                  <span className="text-turquoise">{t.hero.h1c}</span>
                ) : (
                  <motion.span
                    className="block text-turquoise"
                    initial={{ y: "110%" }}
                    animate={{ y: "0%" }}
                    transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1], delay: 0.34 }}
                  >
                    {t.hero.h1c}
                  </motion.span>
                )}
              </span>
            </h1>

            <motion.p
              initial={reduce ? false : { opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.5 }}
              className="mt-6 max-w-xl text-lg leading-relaxed text-navy/70"
            >
              {t.hero.sub}
            </motion.p>

            <motion.div
              initial={reduce ? false : { opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.62 }}
              className="mt-8 flex flex-col gap-3 sm:flex-row"
            >
              <Link
                to={p("contact")}
                data-testid="hero-plan-btn"
                className="inline-flex items-center justify-center gap-2 rounded-full bg-dental px-7 py-4 text-base font-semibold text-white shadow-soft transition-all hover:bg-dental-dark hover:scale-[1.02]"
              >
                {t.cta.myFreePlan}
                <ArrowRight className="h-5 w-5" />
              </Link>
              <a
                href={WHATSAPP_LINK()}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="hero-whatsapp-btn"
                className="inline-flex items-center justify-center gap-2 rounded-full border-2 border-navy/15 px-7 py-4 text-base font-semibold text-navy transition-colors hover:border-turquoise hover:text-turquoise"
              >
                <MessageCircle className="h-5 w-5" />
                {t.cta.whatsapp}
              </a>
            </motion.div>

            <motion.div
              initial={reduce ? false : { opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.7, delay: 0.8 }}
              className="mt-6 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-navy/60"
            >
              {[t.hero.microA, t.hero.microB, t.hero.microC].map((m, i) => (
                <span key={i} className="inline-flex items-center gap-1.5">
                  <Check className="h-4 w-4 text-turquoise" />
                  {m}
                </span>
              ))}
            </motion.div>
          </div>

          {/* Hero image with parallax float */}
          <div className="lg:col-span-6 flex items-center">
            <motion.div
              initial={reduce ? false : { opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1, ease: [0.16, 1, 0.3, 1], delay: 0.3 }}
              className="relative w-full"
            >
              <div className="relative overflow-hidden rounded-[2rem] shadow-lift">
                <img
                  src="https://images.unsplash.com/photo-1777331903190-341a3dd0441b?crop=entropy&cs=srgb&fm=jpg&q=85&w=1100"
                  alt="A dental professional welcoming a mature international patient in a modern clinic"
                  className="h-[26rem] w-full object-cover sm:h-[34rem]"
                  fetchPriority="high"
                />
              </div>
              {/* Floating trust chips */}
              {!reduce && (
                <motion.div
                  animate={{ y: [0, -10, 0] }}
                  transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
                  className="absolute -bottom-5 -left-3 hidden rounded-2xl bg-white p-4 shadow-lift sm:block"
                >
                  <div className="flex items-center gap-3">
                    <span className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-turquoise/10 text-turquoise">
                      <ShieldCheck className="h-5 w-5" />
                    </span>
                    <div>
                      <p className="text-sm font-semibold text-navy">{t.hero.trust[0]}</p>
                      <p className="text-xs text-navy/55">{t.hero.trust[2]}</p>
                    </div>
                  </div>
                </motion.div>
              )}
            </motion.div>
          </div>
        </div>

        {/* trust features strip */}
        <div className="container-dv relative pb-14">
          <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
            {t.hero.trust.map((label, i) => {
              const Icon = TRUST_ICONS[i];
              return (
                <div key={i} className="flex items-center gap-3 rounded-2xl bg-white/70 px-4 py-4 backdrop-blur">
                  <Icon className="h-5 w-5 shrink-0 text-dental" />
                  <span className="text-sm font-semibold text-navy/80">{label}</span>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ============ MARQUEE ============ */}
      <div className="border-y border-navy/8 bg-navy py-5" data-testid="marquee">
        <Marquee gradient={false} speed={38} pauseOnHover>
          {t.marquee.concat(t.marquee).map((m, i) => (
            <span key={i} className="mx-8 inline-flex items-center gap-8 text-sm font-semibold uppercase tracking-[0.25em] text-white/60">
              {m}
              <span className="text-turquoise">◆</span>
            </span>
          ))}
        </Marquee>
      </div>

      {/* ============ POPULAR TREATMENTS ============ */}
      <section className="container-dv py-20 lg:py-28" data-testid="treatments-section">
        <Reveal>
          <div className="flex flex-col gap-6 sm:flex-row sm:items-end sm:justify-between">
            <SectionHeading eyebrow={t.treatmentsSection.eyebrow} title={t.treatmentsSection.title} sub={t.treatmentsSection.sub} />
            <Link to={p("dentalTreatment")} data-testid="treatments-viewall" className="inline-flex shrink-0 items-center gap-1.5 text-sm font-semibold text-dental hover:text-dental-dark">
              {t.cta.allTreatments}
              <ArrowUpRight className="h-4 w-4" />
            </Link>
          </div>
        </Reveal>
        <RevealStagger className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {TREATMENTS.map((tr, i) => (
            <RevealItem key={tr.key} className={tr.featured && i < 3 ? "sm:col-span-2 lg:col-span-1" : ""}>
              <TreatmentCard treatment={tr} />
            </RevealItem>
          ))}
        </RevealStagger>
      </section>

      {/* ============ WHY VIETNAM MANIFESTO ============ */}
      <section className="bg-navy py-20 lg:py-28 relative overflow-hidden grain" data-testid="why-vietnam">
        <div className="container-dv relative">
          <Reveal>
            <SectionHeading eyebrow={t.whyVietnam.eyebrow} title={t.whyVietnam.title} sub={t.whyVietnam.sub} light />
          </Reveal>
          <RevealStagger className="mt-14 grid gap-x-10 gap-y-12 sm:grid-cols-2 lg:grid-cols-3">
            {t.whyVietnam.chapters.map((c, i) => (
              <RevealItem key={i}>
                <div className="border-t border-white/15 pt-6">
                  <span className="font-serif text-5xl text-turquoise/80">{String(i + 1).padStart(2, "0")}</span>
                  <h3 className="mt-4 text-xl text-white">{c.t}</h3>
                  <p className="mt-3 text-[15px] leading-relaxed text-white/65">{c.d}</p>
                </div>
              </RevealItem>
            ))}
          </RevealStagger>
          <Reveal delay={0.1}>
            <p className="mt-12 max-w-3xl rounded-2xl bg-white/5 p-5 text-sm leading-relaxed text-white/60">
              {t.whyVietnam.note}
            </p>
          </Reveal>
        </div>
      </section>

      {/* ============ HOW IT WORKS ============ */}
      <section className="container-dv py-20 lg:py-28" data-testid="how-it-works">
        <Reveal>
          <SectionHeading eyebrow={t.howItWorks.eyebrow} title={t.howItWorks.title} sub={t.howItWorks.sub} />
        </Reveal>
        <RevealStagger className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {t.howItWorks.steps.map((s, i) => (
            <RevealItem key={i}>
              <div className="group h-full rounded-3xl border border-navy/8 bg-white p-7 shadow-soft transition-all hover:shadow-lift hover:-translate-y-1">
                <div className="flex items-center gap-4">
                  <span className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-dental text-lg font-bold text-white">
                    {i + 1}
                  </span>
                  <div className="h-px flex-1 bg-navy/10" />
                </div>
                <h3 className="mt-5 text-xl text-navy">{s.t}</h3>
                <p className="mt-2 text-[15px] leading-relaxed text-navy/65">{s.d}</p>
              </div>
            </RevealItem>
          ))}
        </RevealStagger>
      </section>

      {/* ============ TOP LOCATIONS ============ */}
      <section className="bg-mist py-20 lg:py-28" data-testid="locations-section">
        <div className="container-dv">
          <Reveal>
            <SectionHeading eyebrow={t.locationsSection.eyebrow} title={t.locationsSection.title} sub={t.locationsSection.sub} />
          </Reveal>
          <RevealStagger className="mt-12 grid gap-6 md:grid-cols-3">
            {LOCATIONS.map((lo) => (
              <RevealItem key={lo.key}>
                <LocationCard location={lo} />
              </RevealItem>
            ))}
          </RevealStagger>
        </div>
      </section>

      {/* ============ TRUST / SAFETY ============ */}
      <section className="container-dv py-20 lg:py-28" data-testid="trust-section">
        <div className="grid gap-12 lg:grid-cols-12 lg:gap-16">
          <div className="lg:col-span-5">
            <Reveal>
              <SectionHeading eyebrow={t.trust.eyebrow} title={t.trust.title} sub={t.trust.sub} />
              <Link
                to={p("patientGuide")}
                data-testid="trust-cta"
                className="mt-8 inline-flex items-center gap-2 rounded-full bg-navy px-6 py-3.5 text-sm font-semibold text-white hover:bg-navy-light transition-colors"
              >
                {t.trust.cta}
                <ArrowRight className="h-4 w-4" />
              </Link>
            </Reveal>
          </div>
          <div className="lg:col-span-7">
            <RevealStagger className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              {t.trust.checklist.map((c, i) => (
                <RevealItem key={i}>
                  <div className="flex items-center gap-3 rounded-2xl border border-navy/8 bg-white px-5 py-4 shadow-soft">
                    <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-turquoise/10 text-turquoise">
                      <Check className="h-4 w-4" />
                    </span>
                    <span className="text-[15px] font-semibold text-navy/80">{c}</span>
                  </div>
                </RevealItem>
              ))}
            </RevealStagger>
          </div>
        </div>
      </section>

      {/* ============ REVIEWS (placeholder — no fabricated reviews) ============ */}
      <section className="bg-mist py-20 lg:py-28" data-testid="reviews-section">
        <div className="container-dv">
          <Reveal>
            <SectionHeading eyebrow={t.reviews.eyebrow} title={t.reviews.title} align="center" />
            <div className="mx-auto mt-8 max-w-2xl rounded-3xl border border-dashed border-navy/20 bg-white p-10 text-center">
              <p className="text-navy/65 leading-relaxed">{t.reviews.empty}</p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ============ FAQ ============ */}
      <section className="container-dv py-20 lg:py-28" data-testid="faq-section">
        <div className="grid gap-10 lg:grid-cols-12 lg:gap-16">
          <div className="lg:col-span-4">
            <Reveal>
              <SectionHeading eyebrow={t.faqSection.eyebrow} title={t.faqSection.title} sub={t.faqSection.sub} />
              <Link to={p("faq")} data-testid="faq-viewall" className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-dental hover:text-dental-dark">
                {t.faqSection.viewAll}
                <ArrowUpRight className="h-4 w-4" />
              </Link>
            </Reveal>
          </div>
          <div className="lg:col-span-8">
            <Reveal delay={0.1}>
              <Accordion type="single" collapsible className="space-y-3" data-testid="faq-accordion">
                {FAQS.slice(0, 6).map((item, i) => (
                  <AccordionItem
                    key={i}
                    value={`faq-${i}`}
                    className="rounded-2xl border border-navy/10 bg-white px-5 shadow-soft data-[state=open]:border-dental/30"
                  >
                    <AccordionTrigger data-testid={`faq-trigger-${i}`} className="py-5 text-left text-base font-semibold text-navy hover:no-underline">
                      {item.q[lang] || item.q.en}
                    </AccordionTrigger>
                    <AccordionContent className="pb-5 text-[15px] leading-relaxed text-navy/70">
                      {item.a[lang] || item.a.en}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ============ BLOG PREVIEW ============ */}
      <section className="bg-mist py-20 lg:py-28" data-testid="blog-section">
        <div className="container-dv">
          <Reveal>
            <div className="flex flex-col gap-6 sm:flex-row sm:items-end sm:justify-between">
              <SectionHeading eyebrow={t.blogSection.eyebrow} title={t.blogSection.title} sub={t.blogSection.sub} />
              <Link to={p("blog")} data-testid="blog-viewall" className="inline-flex shrink-0 items-center gap-1.5 text-sm font-semibold text-dental hover:text-dental-dark">
                {t.blogSection.viewAll}
                <ArrowUpRight className="h-4 w-4" />
              </Link>
            </div>
          </Reveal>
          <RevealStagger className="mt-12 grid gap-6 md:grid-cols-2">
            {ARTICLES.map((ar) => (
              <RevealItem key={ar.key}>
                <ArticleCard article={ar} />
              </RevealItem>
            ))}
          </RevealStagger>
        </div>
      </section>

      <CTABand />
    </>
  );
}
